import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { T } from 'Features/SellerCommunications/utils/translate';
import { patchSellerCommunicationMarkAsContacted } from 'Pages/api/seller-communications/mark-as-contacted/client.api';
import { SellerCommunicationStatus } from 'Pages/api/seller-communications/types';
import { triggerSellerMessagingConfirmMarkAsContactedButtonClickedEvent } from 'Services/analytics/events';
import type { CreateLabelParams } from 'Services/analytics/events/account/purchases/helpers';

import { SELLER_COMMUNICATION_ERROR_GROUPS, TOAST_DURATION } from './constants';
import { groupFailedResults, resetStoresState } from './helpers';
import { infoService } from './infoService';
import {
	EnquiriesSearchStore,
	EnquiriesSelectStore,
	EnquiriesStore,
	MarkAsContactedStore,
	ModalFlowStore,
} from './stores';
import { ModalSteps } from './types';

const triggerToastOnSuccess = (count: number) => {
	toast({
		content: T.translate(`markAsContacted.success.description`),
		duration: TOAST_DURATION,
		heading: T.translate(`markAsContacted.success.title.${count === 1 ? 'single' : 'multiple'}`, {
			count,
		}),
		variant: 'success',
	});
};

const triggerToastOnFailure = (count: number) => {
	toast({
		content: T.translate(`markAsContacted.error.toast.description.${count === 1 ? 'single' : 'multiple'}`, { count }),
		duration: TOAST_DURATION,
		heading: T.translate(`markAsContacted.error.toast.title`),
		variant: 'error',
	});
};

const resetStateOnModalClose = () => {
	resetStoresState();
	infoService.updateCommunicationInfo();
};

const proceedOnSuccess = () => {
	resetStateOnModalClose();
};

const proceedOnPartialSuccess = (result: Awaited<ReturnType<typeof patchSellerCommunicationMarkAsContacted>>) => {
	const { enquiries } = EnquiriesStore.getState();

	const errorGroups = groupFailedResults({
		enquiries,
		errors: result.failedToUpdateSales,
		groups: SELLER_COMMUNICATION_ERROR_GROUPS,
		translatePath: 'markAsContacted.error.status',
	});

	MarkAsContactedStore.setState({ errorGroups, result });
	ModalFlowStore.setState({ currentStep: ModalSteps.MarkAsContactedError });
	EnquiriesSearchStore.setState({ filteredSaleIds: {}, keyword: '', noResults: false, search: '' });
	infoService.updateCommunicationInfo();
};

const triggerAnalyticsEvent = (
	{ failedToUpdateEnquiries, updatedEnquiryIds }: CreateLabelParams,
	isSuccess: boolean,
) => {
	triggerSellerMessagingConfirmMarkAsContactedButtonClickedEvent({
		buttonLabel: 'Mark as contacted',
		response: {
			failedToUpdateEnquiries,
			updatedEnquiryIds,
		},
		success: isSuccess,
		url: typeof window !== 'undefined' ? window.location.href : '',
	});
};

const onMarkAsContacted = async () => {
	const { enquiriesMap } = EnquiriesStore.getState();
	ModalFlowStore.setState({ currentStep: ModalSteps.MarkAsContactedLoading });
	const { selectedSaleIds } = EnquiriesSelectStore.getState();

	const saleIds = selectedSaleIds.map((id) => enquiriesMap[id].saleId);

	try {
		const records = saleIds.map((saleId) => ({
			saleId,
			status: SellerCommunicationStatus.DEALER_MARKED_AS_CONTACTED as const,
		}));

		const response = await patchSellerCommunicationMarkAsContacted({ records });

		const { failedToUpdateSales, updatedSales } = response;

		const isSuccess = failedToUpdateSales.length === 0;

		triggerAnalyticsEvent(
			{
				failedToUpdateEnquiries: failedToUpdateSales.map((sale) => ({
					...sale,
					enquiryId: enquiriesMap[sale.saleId].enquiryId,
					saleId: sale.saleId,
				})),
				updatedEnquiryIds: updatedSales.map((sale) => enquiriesMap[sale.saleId].enquiryId),
			},
			isSuccess,
		);

		const hasFailedToUpdateSales = response.failedToUpdateSales.length > 0;
		if (hasFailedToUpdateSales) {
			proceedOnPartialSuccess({
				failedToUpdateSales,
				updatedSales,
			});
			return;
		}

		triggerToastOnSuccess(response.updatedSales.length);
		proceedOnSuccess();
	} catch {
		resetStateOnModalClose();
		triggerToastOnFailure(selectedSaleIds.length);
		triggerAnalyticsEvent(
			{
				failedToUpdateEnquiries: selectedSaleIds.map((enquiryId) => ({
					enquiryId,
					saleId: enquiriesMap[enquiryId].saleId,
					status: SellerCommunicationStatus.NO_MESSAGE_HAS_BEEN_CREATED,
				})),
				updatedEnquiryIds: [],
			},
			false,
		);
	}
};

export const markAsContactedService = {
	onMarkAsContacted,
};
